
// Site Photos API service for fetching data from Supabase
import { supabase } from "@/integrations/supabase/client";

export interface SitePhoto {
  id: string;
  site_id: string;
  image_url: string;
  alt_text: string | null;
  display_order: number;
  is_hero: boolean | null;
}

// Get photos for a specific site
export const fetchSitePhotos = async (siteId: string): Promise<SitePhoto[]> => {
  try {
    if (!siteId) {
      console.warn('No siteId provided to fetchSitePhotos');
      return [];
    }

    const { data, error } = await supabase
      .from('site_photos')
      .select('*')
      .eq('site_id', siteId)
      .order('display_order', { ascending: true });
    
    if (error) {
      console.error('Error fetching site photos:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Failed to fetch site photos:', error);
    return [];
  }
};

// Add a new photo for a site
export const addSitePhoto = async (photo: Omit<SitePhoto, 'id'>): Promise<SitePhoto | null> => {
  try {
    const { data, error } = await supabase
      .from('site_photos')
      .insert(photo)
      .select()
      .single();
    
    if (error) {
      console.error('Error adding site photo:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Failed to add site photo:', error);
    return null;
  }
};

// Update an existing photo
export const updateSitePhoto = async (id: string, updates: Partial<SitePhoto>): Promise<SitePhoto | null> => {
  try {
    const { data, error } = await supabase
      .from('site_photos')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating site photo:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Failed to update site photo:', error);
    return null;
  }
};

// Delete a site photo
export const deleteSitePhoto = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('site_photos')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting site photo:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Failed to delete site photo:', error);
    return false;
  }
};

// Interface for image data
export interface ImageData {
  url: string;
  alt: string;
}

// Utility function to add new uploaded images to the site
export const addUploadedImageToSite = async (
  siteId: string, 
  images: ImageData[] = []
): Promise<void> => {
  try {
    if (!siteId) {
      console.warn('No siteId provided to addUploadedImageToSite');
      return;
    }

    // Check if the site exists in the database first
    const { data: siteExists, error: siteCheckError } = await supabase
      .from('sites')
      .select('id')
      .eq('id', siteId)
      .single();
    
    if (siteCheckError) {
      // Si le site n'existe pas dans la base de données, on ignore silencieusement l'erreur
      // puisqu'on utilise des données locales pour ce site
      console.log("Site n'existe pas en base, utilisation des données locales uniquement");
      return;
    }
    
    // Since the site exists, we can proceed with photos
    // Check if the image already exists for this site
    const existingPhotos = await fetchSitePhotos(siteId);
    
    // Use provided images array or default images
    const imagesToAdd = images.length > 0 ? images : [
      {
        url: "/lovable-uploads/f52eb266-8d83-4f3c-b0e8-48256f324bc4.png",
        alt: "Ouvrier de gestion des déchets triant différents conteneurs colorés"
      },
      {
        url: "/lovable-uploads/806b462a-d931-48b9-8a8f-d12aca6eb522.png",
        alt: "Zone de recyclage avec presse à métaux et véhicules compactés"
      }
    ];
    
    // Delete all existing photos for a clean replacement
    for (const photo of existingPhotos) {
      await deleteSitePhoto(photo.id);
      console.log(`Deleted existing photo ID: ${photo.id}`);
    }
    
    // Add the new photos
    for (const [index, image] of imagesToAdd.entries()) {
      const result = await addSitePhoto({
        site_id: siteId,
        image_url: image.url,
        alt_text: image.alt,
        display_order: index,
        is_hero: index === 0 // First image becomes hero
      });
      
      if (result) {
        console.log(`Added new image: ${image.url}`);
      } else {
        console.warn(`Failed to add image: ${image.url}`);
      }
    }
    
    console.log("Successfully replaced site photos");
  } catch (error) {
    console.error("Failed to replace images:", error);
    // Continue showing the site even if there's an error with the photos
  }
};
